import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@material-ui/core";

import MainHeader from "../../components/MainHeader";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, Add, AccountTree } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { KoraxPage } from "../../components/KoraxUI";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: "1 1 auto",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
        : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch"
    }
  },
  hero: {
    flexShrink: 0,
    borderRadius: 18,
    padding: theme.spacing(1.5, 2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)"
        : "linear-gradient(135deg, #EBF3FF 0%, #D6E9FF 100%)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(0,140,255,0.18)"
        : "1px solid rgba(0,102,255,0.12)",
    boxShadow: "0 6px 20px rgba(0,102,255,0.07)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    position: "relative",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(1.5),
      gap: theme.spacing(1.4),
      borderRadius: 16
    }
  },
  heroBg: {
    position: "absolute", right: -20, top: -20, width: 140, height: 140,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    zIndex: 1,
    minWidth: 0
  },
  heroIcon: {
    width: 48, height: 48, borderRadius: 14, flexShrink: 0, fontSize: "1.3rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 16px rgba(0,102,255,0.3)",
  },
  heroTitle: { fontWeight: 800, fontSize: "1.1rem", color: theme.palette.type === "dark" ? "#fff" : "#0A1628", letterSpacing: "-0.3px" },
  heroSub: { fontSize: "0.75rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.55)", marginTop: 2 },
  heroRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    zIndex: 1,
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between",
      width: "100%"
    }
  },
  heroChip: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
    border: "1px solid rgba(0,102,255,0.15)", borderRadius: 10,
    fontWeight: 700, fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#0A1628",
  },
  addBtn: {
    minHeight: 40,
    borderRadius: 11,
    fontWeight: 800,
    fontSize: ".75rem",
    textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#FFFFFF",
    padding: theme.spacing(.8, 1.8),
    boxShadow: "0 6px 16px rgba(0,102,255,.24)",

    "&:hover": {
      background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)"
    },

    [theme.breakpoints.down("sm")]: {
      minHeight: 38,
      padding: theme.spacing(.7, 1.4)
    }
  },
  actionButton: {
    minHeight: 42, borderRadius: 10, fontWeight: 600, fontSize: "0.75rem",
    padding: theme.spacing(0.8, 1.4), boxShadow: "0 6px 14px rgba(7,64,171,0.14)",
  },
  mainPaper: {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "#FFFFFF",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.07)"
        : "1px solid #E2E8F0",
    boxShadow: "0 10px 28px rgba(15,23,42,.06)",

    [theme.breakpoints.down("sm")]: {
      flex: "0 0 auto",
      overflow: "visible",
      borderRadius: 16,
      background: "transparent",
      border: 0,
      boxShadow: "none"
    }
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15, 23, 42, 0.03)",
    fontSize: "0.76rem",
  },
  tableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15, 23, 42, 0.035)",
    },
  },
  tableCellText: {
    fontSize: "0.78rem",
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  colorSwatch: {
    width: 56,
    height: 18,
    borderRadius: 5,
    border: `1px solid ${theme.palette.divider}`,
  },
  actionCell: {
    minWidth: 120,
  },
  actionIconButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0.25),

    "& svg": {
      fontSize: 17
    }
  },

  desktopOnly: {
    display: "block",
    width: "100%",

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },

  mobileOnly: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: "100%"
    }
  },

  mobileCards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },

  queueCard: {
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,.94)"
        : "#FFFFFF",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 28px rgba(0,0,0,.24)"
        : "0 8px 22px rgba(15,23,42,.055)"
  },

  queueCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    minWidth: 0
  },

  queueColorIcon: {
    width: 42,
    height: 42,
    flexShrink: 0,
    borderRadius: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    boxShadow: "0 7px 16px rgba(15,23,42,.16)",

    "& svg": {
      fontSize: 21
    }
  },

  queueIdentity: {
    flex: 1,
    minWidth: 0
  },

  queueName: {
    color: theme.palette.text.primary,
    fontSize: ".93rem",
    fontWeight: 850,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  queueId: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: ".65rem",
    fontWeight: 700
  },

  queueCardActions: {
    display: "flex",
    gap: theme.spacing(.6),
    flexShrink: 0
  },

  cardActionButton: {
    width: 34,
    height: 34,
    padding: 0,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,

    "& svg": {
      fontSize: 18
    }
  },

  editButton: {
    color: "#0066FF"
  },

  deleteButton: {
    color: "#EF4444"
  },

  queueMetaGrid: {
    marginTop: theme.spacing(1.3),
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(.8)
  },

  queueMetaBox: {
    minWidth: 0,
    padding: theme.spacing(1),
    borderRadius: 11,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#F8FAFC",
    border: `1px solid ${theme.palette.divider}`
  },

  queueMetaLabel: {
    color: theme.palette.text.secondary,
    fontSize: ".59rem",
    fontWeight: 800,
    letterSpacing: ".04em",
    textTransform: "uppercase"
  },

  queueMetaValue: {
    marginTop: 5,
    color: theme.palette.text.primary,
    fontSize: ".75rem",
    lineHeight: 1.45,
    fontWeight: 650,
    wordBreak: "break-word"
  },

  emptyMobile: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: ".8rem"
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    }
    return [queue, ...state];
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <KoraxPage className={classes.pageRoot}>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res);
            }, 500);
          }
        }}
      />
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.hero}>
            <div className={classes.heroBg} />
            <div className={classes.heroLeft}>
              <div className={classes.heroIcon}>
                <AccountTree style={{ fontSize: 24, color: "#FFFFFF" }} />
              </div>
              <div>
                <Typography className={classes.heroTitle}>Setores de Atendimento</Typography>
                <Typography className={classes.heroSub}>Organize suas equipes por departamento e automatize o direcionamento</Typography>
              </div>
            </div>
            <div className={classes.heroRight}>
              <Chip
                icon={<AccountTree style={{ fontSize: 14 }} />}
                label={`${queues.length} setores`}
                className={classes.heroChip}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleOpenQueueModal}
                className={classes.addBtn}
                startIcon={<Add style={{ fontSize: 16 }} />}
              >
                Novo setor
              </Button>
            </div>
          </div>

          <Paper className={classes.mainPaper} variant="outlined">
            <div className={classes.desktopOnly}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.ID")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.name")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.color")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.orderQueue")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.greeting")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queues.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {queues.map(queue => (
                    <TableRow key={queue.id} className={classes.tableRow} hover>
                      <TableCell align="center" className={classes.tableCellText}>
                        {queue.id}
                      </TableCell>

                      <TableCell align="center" className={classes.tableCellText}>
                        <strong>{queue.name}</strong>
                      </TableCell>

                      <TableCell align="center">
                        <div className={classes.customTableCell}>
                          <span
                            className={classes.colorSwatch}
                            style={{ backgroundColor: queue.color || "#0066FF" }}
                          />
                        </div>
                      </TableCell>

                      <TableCell align="center" className={classes.tableCellText}>
                        <div className={classes.customTableCell}>
                          <Typography
                            style={{ width: 260 }}
                            noWrap
                            variant="body2"
                            className={classes.tableCellText}
                          >
                            {queue.orderQueue || "-"}
                          </Typography>
                        </div>
                      </TableCell>

                      <TableCell align="center" className={classes.tableCellText}>
                        <div className={classes.customTableCell}>
                          <Typography
                            style={{ width: 300 }}
                            noWrap
                            variant="body2"
                            className={classes.tableCellText}
                          >
                            {queue.greetingMessage || "-"}
                          </Typography>
                        </div>
                      </TableCell>

                      <TableCell align="center" className={classes.actionCell}>
                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => handleEditQueue(queue)}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => {
                            setSelectedQueue(queue);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {loading && <TableRowSkeleton columns={6} />}
                </TableBody>
              </Table>
            </div>

            <div className={classes.mobileOnly}>
              <div className={classes.mobileCards}>
                {queues.map(queue => (
                  <div
                    key={`mobile-queue-${queue.id}`}
                    className={classes.queueCard}
                  >
                    <div className={classes.queueCardHeader}>
                      <div
                        className={classes.queueColorIcon}
                        style={{
                          backgroundColor: queue.color || "#0066FF"
                        }}
                      >
                        <AccountTree />
                      </div>

                      <div className={classes.queueIdentity}>
                        <Typography className={classes.queueName}>
                          {queue.name || "Setor sem nome"}
                        </Typography>

                        <Typography className={classes.queueId}>
                          ID #{queue.id}
                        </Typography>
                      </div>

                      <div className={classes.queueCardActions}>
                        <IconButton
                          className={`${classes.cardActionButton} ${classes.editButton}`}
                          onClick={() => handleEditQueue(queue)}
                          aria-label={`Editar ${queue.name}`}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          className={`${classes.cardActionButton} ${classes.deleteButton}`}
                          onClick={() => {
                            setSelectedQueue(queue);
                            setConfirmModalOpen(true);
                          }}
                          aria-label={`Excluir ${queue.name}`}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </div>
                    </div>

                    <div className={classes.queueMetaGrid}>
                      <div className={classes.queueMetaBox}>
                        <div className={classes.queueMetaLabel}>
                          Ordem da fila
                        </div>

                        <div className={classes.queueMetaValue}>
                          {queue.orderQueue || "Não definida"}
                        </div>
                      </div>

                      <div className={classes.queueMetaBox}>
                        <div className={classes.queueMetaLabel}>
                          Mensagem de saudação
                        </div>

                        <div className={classes.queueMetaValue}>
                          {queue.greetingMessage || "Nenhuma mensagem configurada"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!loading && queues.length === 0 && (
                  <div className={classes.emptyMobile}>
                    Nenhum setor cadastrado.
                  </div>
                )}

                {loading && (
                  <div className={classes.emptyMobile}>
                    Carregando setores...
                  </div>
                )}
              </div>
            </div>
          </Paper>
        </>
      )}
    </KoraxPage>
  );
};

export default Queues;
